// A24 — Decision Archaeology + Replay. Decisions list, drift alerts,
// ask-the-ledger surface. Replay-by-agenda lives inside RoadTab as a
// per-agenda button; this panel is the cross-cutting view.

import { useEffect, useState } from "react";
import {
  ApiError,
  askTheLedger,
  listDecisions,
  listDriftAlerts,
  resolveDriftAlert,
  type AskTheLedgerResponse,
  type DecisionRow,
  type DriftAlertRow,
} from "../api/client";

interface Props {
  workspaceId: number;
}

export function ArchaeologyPanel({ workspaceId }: Props) {
  const [decisions, setDecisions] = useState<DecisionRow[]>([]);
  const [alerts, setAlerts] = useState<DriftAlertRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [designOnly, setDesignOnly] = useState(false);

  const refresh = async () => {
    setBusy(true);
    setError(null);
    try {
      const [d, a] = await Promise.all([
        listDecisions(workspaceId, {
          designArchaeology: designOnly || undefined,
          limit: 100,
        }),
        listDriftAlerts(workspaceId),
      ]);
      setDecisions(d);
      setAlerts(a);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, designOnly]);

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-edge bg-surface-raised p-3"
      data-testid="archaeology-panel"
    >
      <header>
        <h3 className="text-xs font-semibold text-primary">
          Decision archaeology
        </h3>
        <p className="mt-1 text-[0.625rem] text-muted">
          Why did we do this? What changed our mind? Decisions are
          first-class rows; supersession preserves the chain.
        </p>
      </header>

      <AskTheLedgerForm workspaceId={workspaceId} />

      <section>
        <header className="flex items-center justify-between border-b border-edge pb-1">
          <h4 className="text-[0.625rem] font-medium text-muted">Drift alerts</h4>
          <span className="text-[0.625rem] text-faint">
            {alerts.filter((a) => a.status === "open").length} open
          </span>
        </header>
        {alerts.length === 0 ? (
          <p className="mt-1 text-[0.625rem] text-faint">
            No drift alerts. drift_detector_pass scans hourly.
          </p>
        ) : (
          <ul className="mt-1 flex flex-col gap-1">
            {alerts.slice(0, 20).map((a) => (
              <DriftAlertRowView
                key={a.alert_id}
                row={a}
                workspaceId={workspaceId}
                onChange={() => void refresh()}
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        <header className="flex items-center justify-between border-b border-edge pb-1">
          <h4 className="text-[0.625rem] font-medium text-muted">Decisions</h4>
          <label className="flex items-center gap-1 text-[0.625rem] text-muted">
            <input
              type="checkbox"
              checked={designOnly}
              onChange={(e) => setDesignOnly(e.target.checked)}
            />
            design archaeology only
          </label>
        </header>
        {busy && <p className="mt-1 text-[0.625rem] text-muted">Loading…</p>}
        {error && <p className="mt-1 text-[0.625rem] text-danger">{error}</p>}
        {decisions.length === 0 && !busy ? (
          <p className="mt-1 text-[0.625rem] text-faint">No decisions yet.</p>
        ) : (
          <ul className="mt-1 flex flex-col gap-1">
            {decisions.map((d) => (
              <li
                key={d.decision_id}
                className="rounded-md border border-edge bg-surface px-2 py-1"
                data-testid={`decision-${d.decision_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-primary">
                    {d.title}
                  </span>
                  <span className="font-code text-[0.625rem] text-faint">
                    {d.kind}
                    {d.design_archaeology && " · design"}
                  </span>
                </div>
                <p className="mt-0.5 line-clamp-2 text-[0.625rem] text-muted">
                  {(d.body_md ?? "").replace(/^#+\s*/gm, "")}
                </p>
                <div className="mt-0.5 flex items-baseline justify-between gap-2">
                  <span className="font-code text-[0.625rem] text-faint">
                    {d.author_kind}
                    {d.author_agent_id ? ` · agent ${d.author_agent_id}` : ""}
                  </span>
                  {d.superseded_by != null && (
                    <span className="font-code text-[0.625rem] text-warning">
                      superseded → {d.superseded_by}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function AskTheLedgerForm({ workspaceId }: { workspaceId: number }) {
  const [question, setQuestion] = useState("");
  const [resp, setResp] = useState<AskTheLedgerResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!question.trim()) return;
    setBusy(true);
    setError(null);
    setResp(null);
    try {
      const r = await askTheLedger(workspaceId, { question: question.trim() });
      setResp(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md border border-edge bg-surface px-3 py-2"
      data-testid="ask-the-ledger"
    >
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder='Ask the ledger ("Why did we choose X?", "What changed our mind?")…'
          className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
          data-testid="ask-the-ledger-input"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !question.trim()}
          className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
        >
          {busy ? "Asking…" : "Ask"}
        </button>
      </div>
      {error && <p className="mt-1 text-[0.625rem] text-danger">{error}</p>}
      {resp && (
        <article
          className="mt-2 rounded-md border-l-2 border-accent bg-surface-raised px-3 py-2"
          data-testid="ask-the-ledger-answer"
        >
          <pre className="whitespace-pre-wrap text-xs text-primary">
            {resp.answer_md}
          </pre>
          <p className="mt-2 text-[0.625rem] text-muted">
            <span className="text-warning">Uncertainty: </span>
            {resp.uncertainty}
          </p>
          <p className="mt-1 text-[0.625rem] text-muted">
            <span className="text-accent">Next: </span>
            {resp.suggested_next_action}
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer text-[0.625rem] text-muted hover:text-primary">
              evidence_refs
            </summary>
            <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap font-code text-[0.625rem] text-faint">
              {JSON.stringify(resp.evidence_refs, null, 2)}
            </pre>
          </details>
        </article>
      )}
    </div>
  );
}

function DriftAlertRowView({
  row,
  workspaceId,
  onChange,
}: {
  row: DriftAlertRow;
  workspaceId: number;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const resolve = async () => {
    setBusy(true);
    try {
      await resolveDriftAlert(workspaceId, row.alert_id);
      onChange();
    } finally {
      setBusy(false);
    }
  };
  const tone =
    row.status === "resolved" ? "border-edge" : "border-warning/60";
  return (
    <li
      className={`rounded-md border-l-2 ${tone} bg-surface px-2 py-1`}
      data-testid={`drift-alert-${row.alert_id}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-primary">{row.summary}</span>
        <span className="font-code text-[0.625rem] text-faint">{row.kind}</span>
      </div>
      <div className="mt-0.5 flex items-baseline justify-between gap-2">
        <span className="font-code text-[0.625rem] text-faint">
          {new Date(row.created_at / 1000).toLocaleString()}
        </span>
        {row.status === "open" && (
          <button
            type="button"
            onClick={() => void resolve()}
            disabled={busy}
            className="rounded-md border border-edge px-2 py-0.5 text-[0.625rem] text-muted hover:border-success hover:text-success disabled:opacity-50"
          >
            {busy ? "…" : "Resolve"}
          </button>
        )}
        {row.status === "resolved" && (
          <span className="text-[0.625rem] text-success">resolved</span>
        )}
      </div>
    </li>
  );
}
